import { logger } from 'common/services';

import { forcedLogout } from './forcedLogout';

import type Keycloak from 'keycloak-js';

const MIN_VALIDITY = 60;

/**
 * Logs out a user who did not ask to be logged out - their session expired or was refused - and
 * brings them back to the page they were on. Signed out there, `ProtectedRoute` sends them to the
 * login page with that page as `redirectUri`, so signing back in returns them to it instead of to
 * the home page. The hash is left off: it is empty, or Keycloak's own callback parameters.
 *
 * Before leaving, the logout is marked as forced and pages keep what they registered to keep (see
 * `forcedLogout`). A logout the user chose still goes to the home page; see `keycloak.logout`.
 */
export const logoutToCurrentPage = (keycloak: Pick<Keycloak, 'logout'>) => {
  forcedLogout.begin();

  return keycloak.logout({
    redirectUri: window.location.origin + window.location.pathname + window.location.search,
  });
};

export const updateKeycloakToken = async (keycloak: Keycloak) => {
  try {
    await keycloak.updateToken(MIN_VALIDITY);
  } catch (error) {
    if (error instanceof Error) {
      logger.error('[KC] Failed to refresh the token, or the session has expired', error);
    }
    await logoutToCurrentPage(keycloak);
  }
};

/**
 * The Keycloak login session the current tokens belong to. It stays the same across check-sso
 * refreshes and page reloads, and changes on every new login - so it identifies "this login".
 */
export const getKeycloakSessionState = (
  keycloak: Pick<Keycloak, 'idTokenParsed' | 'tokenParsed' | 'sessionId'>
): string | undefined =>
  (keycloak.idTokenParsed as { session_state?: string } | undefined)?.session_state ??
  (keycloak.tokenParsed as { session_state?: string } | undefined)?.session_state ??
  keycloak.sessionId;
